import { useEffect, useState } from "react";
import { endpoints, formatApiError } from "../../lib/api";
import { formatCurrency } from "../../lib/romEngine";
import { BillingStatusBadge } from "../../components/BillingStatusBadge";
import {
    Receipt, Plus, X, CircleNotch, PaperPlaneTilt, Copy, CheckCircle,
    CurrencyDollar, Trash, LinkSimple,
} from "@phosphor-icons/react";

const STATUS_META = {
    draft: { cls: "chip-gray", label: "Draft" },
    sent: { cls: "chip-orange", label: "Sent" },
    partially_paid: { cls: "chip-navy", label: "Partially paid" },
    paid: { cls: "chip-green", label: "Paid" },
    overdue: { cls: "chip-orange", label: "Overdue" },
};

function shareUrl(token) {
    return `${window.location.origin}/share/${token}`;
}

export function InvoicesPanel({ project, canEdit, onChanged }) {
    const [invoices, setInvoices] = useState(null);
    const [showCreate, setShowCreate] = useState(false);
    const [sendFor, setSendFor] = useState(null);
    const [payFor, setPayFor] = useState(null);
    const [err, setErr] = useState("");

    async function reload() {
        try {
            const { data } = await endpoints.invoices(project.id);
            setInvoices(data);
            onChanged?.();
        } catch (e) { setErr(formatApiError(e)); }
    }
    useEffect(() => { reload(); /* eslint-disable-next-line */ }, [project.id]);

    const basis = project.contract_value || project.rom_value || 0;
    const invoicedPercent = (invoices || []).reduce((s, i) => s + (i.percent || 0), 0);
    const remainingPercent = Math.max(0, Math.round((100 - invoicedPercent) * 100) / 100);

    async function voidInvoice(inv) {
        if (!window.confirm(`Void invoice ${inv.invoice_number}? This cannot be undone.`)) return;
        try { await endpoints.voidInvoice(inv.id); await reload(); }
        catch (e) { alert(formatApiError(e)); }
    }

    return (
        <div className="rom-card" data-testid="invoices-panel">
            <div className="rom-card-head">
                <h3><Receipt size={14} weight="bold" /> Invoices</h3>
                {canEdit && basis > 0 && (
                    <button onClick={() => setShowCreate(true)} className="btn btn-primary btn-sm" data-testid="convert-to-invoice-btn">
                        <Plus size={12} weight="bold" /> Convert to invoice
                    </button>
                )}
            </div>
            <div className="rom-card-body space-y-3">
                {basis <= 0 && (
                    <div className="text-xs text-[var(--rom-text-3)]">Set a contract or ROM value first — invoices are billed as a % of that basis.</div>
                )}
                {err && <div className="text-sm text-[var(--rom-danger)]">{err}</div>}
                {basis > 0 && invoices && invoices.length > 0 && remainingPercent > 0 && (
                    <div className="text-[11px] text-[var(--rom-text-2)] bg-slate-50 border border-[var(--rom-border)] rounded px-3 py-1.5" data-testid="remaining-percent">
                        <b>{remainingPercent.toFixed(0)}%</b> of the contract value has not been invoiced yet.
                    </div>
                )}
                {invoices === null ? (
                    <div className="text-center py-6 text-[var(--rom-text-3)]"><CircleNotch size={18} className="animate-spin inline" /></div>
                ) : invoices.length === 0 ? (
                    <div className="text-center py-8 text-sm text-[var(--rom-text-3)]" data-testid="invoices-empty">No invoices yet.</div>
                ) : (
                    <div className="overflow-x-auto">
                        <table className="rom-table">
                            <thead><tr><th>Invoice</th><th>Basis</th><th className="numeric">Total</th><th className="numeric">Paid</th><th className="numeric">Balance</th><th>Due</th><th>Status</th><th></th></tr></thead>
                            <tbody>
                                {invoices.map((inv) => {
                                    const meta = STATUS_META[inv.display_status] || STATUS_META.draft;
                                    return (
                                        <tr key={inv.id} data-testid={`invoice-row-${inv.id}`}>
                                            <td className="font-mono text-xs font-semibold">{inv.invoice_number}</td>
                                            <td className="text-xs text-[var(--rom-text-2)]">{inv.billing_mode === "full" ? "100%" : `${inv.percent}%`}{inv.jurisdiction ? ` · ${inv.jurisdiction}` : ""}</td>
                                            <td className="numeric font-semibold">{formatCurrency(inv.total)}</td>
                                            <td className="numeric text-[var(--rom-green)]">{formatCurrency(inv.paid_amount)}</td>
                                            <td className="numeric">{formatCurrency(inv.balance)}</td>
                                            <td className="text-xs">{inv.due_date}</td>
                                            <td><span className={`chip ${meta.cls}`} style={{ padding: "1px 6px", fontSize: "10px" }} data-testid={`invoice-status-${inv.id}`}>{meta.label}</span></td>
                                            <td>
                                                {canEdit && (
                                                    <div className="flex items-center justify-end gap-1">
                                                        <button onClick={() => setSendFor(inv)} className="btn btn-ghost btn-sm" data-testid={`invoice-send-${inv.id}`} title="Send">
                                                            <PaperPlaneTilt size={12} weight="bold" />
                                                        </button>
                                                        {inv.balance > 0.01 && (
                                                            <button onClick={() => setPayFor(inv)} className="btn btn-ghost btn-sm" data-testid={`invoice-pay-${inv.id}`} title="Record payment">
                                                                <CurrencyDollar size={12} weight="bold" />
                                                            </button>
                                                        )}
                                                        <button onClick={() => voidInvoice(inv)} className="btn btn-ghost btn-sm text-red-600" data-testid={`invoice-void-${inv.id}`} title="Void">
                                                            <Trash size={12} weight="bold" />
                                                        </button>
                                                    </div>
                                                )}
                                            </td>
                                        </tr>
                                    );
                                })}
                            </tbody>
                        </table>
                    </div>
                )}
            </div>
            {showCreate && <CreateInvoiceModal project={project} basis={basis} remainingPercent={remainingPercent} onClose={() => setShowCreate(false)} onCreated={() => { setShowCreate(false); reload(); }} />}
            {sendFor && <SendInvoiceModal invoice={sendFor} onClose={() => setSendFor(null)} />}
            {payFor && <RecordPaymentModal invoice={payFor} onClose={() => setPayFor(null)} onRecorded={() => { setPayFor(null); reload(); }} />}
        </div>
    );
}

function CreateInvoiceModal({ project, basis, remainingPercent, onClose, onCreated }) {
    const [mode, setMode] = useState(remainingPercent >= 99.99 ? "full" : "staged");
    const [percent, setPercent] = useState(remainingPercent >= 99.99 ? 100 : Math.min(30, remainingPercent));
    const [jurisdiction, setJurisdiction] = useState("");
    const [taxRate, setTaxRate] = useState(0);
    const [dueDays, setDueDays] = useState(30);
    const [busy, setBusy] = useState(false);
    const [err, setErr] = useState("");

    const effPercent = mode === "full" ? 100 : Number(percent) || 0;
    const subtotal = round2(basis * effPercent / 100);
    const taxAmount = round2(subtotal * (Number(taxRate) || 0) / 100);
    const total = round2(subtotal + taxAmount);

    async function submit() {
        setBusy(true); setErr("");
        try {
            await endpoints.createInvoiceFromProject(project.id, {
                percent: effPercent, jurisdiction, tax_rate: Number(taxRate) || 0, due_days: Number(dueDays) || 30,
            });
            onCreated();
        } catch (e) { setErr(formatApiError(e)); }
        finally { setBusy(false); }
    }

    return (
        <div className="fixed inset-0 z-50 bg-black/60 flex items-center justify-center p-4" onClick={onClose} data-testid="create-invoice-modal">
            <div className="w-full max-w-md bg-white rounded-[8px] overflow-hidden" onClick={(e) => e.stopPropagation()}>
                <div className="bg-[var(--rom-navy)] text-white px-5 py-4 flex items-center justify-between">
                    <div className="font-display font-bold text-base">Convert to invoice</div>
                    <button onClick={onClose}><X size={16} /></button>
                </div>
                <div className="p-5 space-y-4">
                    <div className="flex gap-2">
                        <button className={`flex-1 px-3 py-2 text-xs font-semibold rounded border ${mode === "full" ? "bg-[var(--rom-navy)] text-white border-[var(--rom-navy)]" : "bg-white border-[var(--rom-border-strong)]"}`}
                            onClick={() => { setMode("full"); setPercent(100); }} data-testid="invoice-mode-full">Full — 100%</button>
                        <button className={`flex-1 px-3 py-2 text-xs font-semibold rounded border ${mode === "staged" ? "bg-[var(--rom-navy)] text-white border-[var(--rom-navy)]" : "bg-white border-[var(--rom-border-strong)]"}`}
                            onClick={() => setMode("staged")} data-testid="invoice-mode-staged">Staged — by %</button>
                    </div>
                    {mode === "staged" && (
                        <div>
                            <label className="label">Percent of contract value</label>
                            <input type="number" min={0.01} max={remainingPercent} step="0.01" className="input" value={percent}
                                onChange={(e) => setPercent(e.target.value)} data-testid="invoice-percent-input" />
                            <p className="text-[11px] text-[var(--rom-text-3)] mt-1">{remainingPercent.toFixed(0)}% of the contract remains uninvoiced.</p>
                        </div>
                    )}
                    <div className="grid grid-cols-2 gap-3">
                        <div>
                            <label className="label">Jurisdiction (state/locality)</label>
                            <input className="input" value={jurisdiction} onChange={(e) => setJurisdiction(e.target.value)} placeholder="e.g. WI" data-testid="invoice-jurisdiction" />
                        </div>
                        <div>
                            <label className="label">Tax rate %</label>
                            <input type="number" min={0} step="0.01" className="input" value={taxRate} onChange={(e) => setTaxRate(e.target.value)} data-testid="invoice-tax-rate" />
                        </div>
                    </div>
                    <div>
                        <label className="label">Due in (days)</label>
                        <input type="number" min={1} className="input" value={dueDays} onChange={(e) => setDueDays(e.target.value)} data-testid="invoice-due-days" />
                    </div>
                    <div className="rounded border border-[var(--rom-border)] bg-slate-50 px-3 py-2 text-sm space-y-1" data-testid="invoice-preview">
                        <div className="flex justify-between"><span className="text-[var(--rom-text-2)]">Subtotal</span><span className="font-mono">{formatCurrency(subtotal)}</span></div>
                        <div className="flex justify-between"><span className="text-[var(--rom-text-2)]">Tax</span><span className="font-mono">{formatCurrency(taxAmount)}</span></div>
                        <div className="flex justify-between font-bold"><span>Total</span><span className="font-mono">{formatCurrency(total)}</span></div>
                    </div>
                    {err && <div className="text-sm text-[var(--rom-danger)]" data-testid="create-invoice-error">{err}</div>}
                    <div className="flex justify-end gap-2">
                        <button className="btn btn-ghost" onClick={onClose}>Cancel</button>
                        <button className="btn btn-primary" onClick={submit} disabled={busy || effPercent <= 0} data-testid="create-invoice-submit">
                            {busy ? <CircleNotch size={14} className="animate-spin" /> : <Plus size={14} weight="bold" />} {busy ? "Creating…" : "Create draft invoice"}
                        </button>
                    </div>
                </div>
            </div>
        </div>
    );
}

function SendInvoiceModal({ invoice, onClose }) {
    const [name, setName] = useState("");
    const [email, setEmail] = useState("");
    const [busy, setBusy] = useState(false);
    const [doc, setDoc] = useState(invoice.share_link ? { share_link: invoice.share_link } : null);
    const [copied, setCopied] = useState(false);
    const [err, setErr] = useState("");

    async function send() {
        setBusy(true); setErr("");
        try {
            const { data } = await endpoints.sendInvoice(invoice.id, { recipient_name: name, recipient_email: email });
            setDoc(data);
        } catch (e) { setErr(formatApiError(e)); }
        finally { setBusy(false); }
    }
    function copy() {
        navigator.clipboard?.writeText(shareUrl(doc.share_link));
        setCopied(true); setTimeout(() => setCopied(false), 2000);
    }

    return (
        <div className="fixed inset-0 z-50 bg-black/60 flex items-center justify-center p-4" onClick={onClose} data-testid="send-invoice-modal">
            <div className="w-full max-w-md bg-white rounded-[8px] overflow-hidden" onClick={(e) => e.stopPropagation()}>
                <div className="bg-[var(--rom-navy)] text-white px-5 py-4 flex items-center justify-between">
                    <div>
                        <div className="font-display font-bold text-base">Send invoice</div>
                        <div className="text-[11px] text-white/60">{invoice.invoice_number} · {formatCurrency(invoice.total)}</div>
                    </div>
                    <button onClick={onClose}><X size={16} /></button>
                </div>
                <div className="p-5 space-y-4">
                    {!doc ? (
                        <>
                            <div><label className="label">Recipient name</label><input className="input" value={name} onChange={(e) => setName(e.target.value)} data-testid="invoice-recipient-name" /></div>
                            <div><label className="label">Recipient email <span className="text-[var(--rom-text-3)] font-normal">(for email — coming soon)</span></label>
                                <input className="input" type="email" value={email} onChange={(e) => setEmail(e.target.value)} data-testid="invoice-recipient-email" /></div>
                            {err && <div className="text-sm text-[var(--rom-danger)]">{err}</div>}
                            <button className="btn btn-primary w-full justify-center" onClick={send} disabled={busy} data-testid="invoice-generate-link">
                                {busy ? <CircleNotch size={15} className="animate-spin" /> : <LinkSimple size={15} weight="bold" />} {busy ? "Generating…" : "Generate secure link"}
                            </button>
                        </>
                    ) : (
                        <div className="space-y-3" data-testid="invoice-link-ready">
                            <div className="flex items-center gap-2 text-[var(--rom-green)] font-semibold text-sm"><CheckCircle size={18} weight="fill" /> Link ready to share</div>
                            <div className="flex items-center gap-2">
                                <input readOnly className="input font-mono text-xs flex-1" value={shareUrl(doc.share_link)} data-testid="invoice-link-value" />
                                <button className="btn btn-ghost btn-sm shrink-0" onClick={copy} data-testid="invoice-copy-link">
                                    {copied ? <CheckCircle size={14} weight="bold" className="text-[var(--rom-green)]" /> : <Copy size={14} weight="bold" />} {copied ? "Copied" : "Copy"}
                                </button>
                            </div>
                            <a href={shareUrl(doc.share_link)} target="_blank" rel="noreferrer" className="text-xs text-[var(--rom-navy)] font-semibold hover:underline">Preview what the client sees →</a>
                        </div>
                    )}
                </div>
            </div>
        </div>
    );
}

function RecordPaymentModal({ invoice, onClose, onRecorded }) {
    const [amount, setAmount] = useState(invoice.balance);
    const [method, setMethod] = useState("bank_transfer");
    const [paidAt, setPaidAt] = useState(new Date().toISOString().slice(0, 10));
    const [notes, setNotes] = useState("");
    const [busy, setBusy] = useState(false);
    const [err, setErr] = useState("");

    async function submit() {
        setBusy(true); setErr("");
        try {
            await endpoints.recordPayment(invoice.id, { amount: Number(amount), method, paid_at: paidAt, notes });
            onRecorded();
        } catch (e) { setErr(formatApiError(e)); }
        finally { setBusy(false); }
    }

    return (
        <div className="fixed inset-0 z-50 bg-black/60 flex items-center justify-center p-4" onClick={onClose} data-testid="record-payment-modal">
            <div className="w-full max-w-sm bg-white rounded-[8px] overflow-hidden" onClick={(e) => e.stopPropagation()}>
                <div className="bg-[var(--rom-navy)] text-white px-5 py-4 flex items-center justify-between">
                    <div>
                        <div className="font-display font-bold text-base">Record payment</div>
                        <div className="text-[11px] text-white/60">{invoice.invoice_number} · balance {formatCurrency(invoice.balance)}</div>
                    </div>
                    <button onClick={onClose}><X size={16} /></button>
                </div>
                <div className="p-5 space-y-3">
                    <div><label className="label">Amount</label><input type="number" min={0.01} max={invoice.balance} step="0.01" className="input" value={amount} onChange={(e) => setAmount(e.target.value)} data-testid="payment-amount" /></div>
                    <div>
                        <label className="label">Method</label>
                        <select className="select" value={method} onChange={(e) => setMethod(e.target.value)} data-testid="payment-method">
                            <option value="bank_transfer">Bank transfer</option>
                            <option value="check">Check</option>
                            <option value="card">Card</option>
                            <option value="other">Other</option>
                        </select>
                    </div>
                    <div><label className="label">Date</label><input type="date" className="input" value={paidAt} onChange={(e) => setPaidAt(e.target.value)} data-testid="payment-date" /></div>
                    <div><label className="label">Notes (optional)</label><input className="input" value={notes} onChange={(e) => setNotes(e.target.value)} data-testid="payment-notes" /></div>
                    {err && <div className="text-sm text-[var(--rom-danger)]" data-testid="payment-error">{err}</div>}
                    <div className="flex justify-end gap-2 pt-1">
                        <button className="btn btn-ghost" onClick={onClose}>Cancel</button>
                        <button className="btn btn-primary" onClick={submit} disabled={busy} data-testid="payment-submit">
                            {busy ? <CircleNotch size={14} className="animate-spin" /> : <CurrencyDollar size={14} weight="bold" />} {busy ? "Recording…" : "Record payment"}
                        </button>
                    </div>
                </div>
            </div>
        </div>
    );
}

function round2(n) { return Math.round((Number(n) || 0) * 100) / 100; }
